import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams } from 'react-router';
import AbundanceView from 'components/abundanceView/AbundanceView';
import { StatsExperimentView, StatsMatrixView } from './statsView/StatsView';
import ButtonSet from './inputs/ButtonSet';
import { MenuTabs } from 'components/menus/MenuTabs';
import { Tab } from 'components/menus/Tab';
import ButtonNew from './inputs/ButtonNew';
import * as urls from 'constants/urls';
import './DetailsPage.scss';
import { usePlayModeState } from '../state/PlayModeState';
import { SettingsDropdown } from 'components/SettingsNew';

const Stats = ({ base, matrix, isExperiment }) => {
  if (!matrix) return null;
  return (
    <div id="charts-download">
      {isExperiment ? (
        <StatsExperimentView base={base} matrix={matrix} />
      ) : (
        <StatsMatrixView matrix={matrix} />
      )}
    </div>
  );
};

const DetailsPageHeaderText = ({ tab }) => {
  if (tab === 'abundance') {
    return (
      <div className="intro-paragraph">
        See how changing the{' '}
        <span className="intro-paragraph-key-word">abundance</span> of an input
        affects the equilibrium below. All results are compared to the original
        matrix settings. Look at the{' '}
        <span className="intro-paragraph-key-word">statistics</span> view for a
        quantitative summary of all changes.
      </div>
    );
  } else {
    return (
      <div className="intro-paragraph">
        The mathematical significance of all differences between the changed
        state and the equilibrium state of the matrix is shown below. View the{' '}
        <span className="intro-paragraph-key-word">sensitivity analysis</span>{' '}
        to investigate individual relationships in more depth.
      </div>
    );
  }
};

// This one does have the modification sidebar
const DetailsPage = ({ base, matrix, goBack }) => {
  const downloadRef = useRef(null);
  const [toggle, setToggle] = useState('abundance');

  const nodeResultsState = usePlayModeState(state => state.nodeResults);

  const isExperiment = useMemo(() => {
    return !!base;
  }, [base]);

  const backUrl = useMemo(() => {
    if (!isExperiment) {
      return urls.matrix(matrix);
    }
    if (isExperiment) {
      return urls.experiment(matrix);
    }
    return null;
  }, [isExperiment, matrix]);

  const onDownload = () => {
    if (downloadRef.current) {
      downloadRef.current.downloadData();
    }
  };

  const options = [
    {
      label: 'Abundance',
      value: 'abundance',
      icon: 'abundance',
      className: '',
    },
    {
      label: 'Statistics',
      value: 'stats',
      icon: 'solidStatistics',
      className: '',
    },
  ];

  let [pointOfComparison, setPointOfComparison] = useState(
    nodeResultsState ? 'results' : 'experiment'
  );

  return (
    <div id="details-page" className="DetailsPage">
      <div className="DetailsPage-top-bar">
        <div className="DetailsPage-top-bar-left">
          {/* TODO make this a dropdown for results */}
          <div className="DetailsPage-header">
            Compare to{' '}
            {nodeResultsState && matrix?.parentId ? (
              <SettingsDropdown
                onChange={inputValue => {
                  setPointOfComparison(inputValue);
                }}
                options={[
                  { label: 'Results', value: 'results' },
                  {
                    label: 'Base matrix',
                    value: 'experiment',
                  },
                ]}
                value={pointOfComparison}
              />
            ) : nodeResultsState ? (
              'Results'
            ) : (
              'Base matrix'
            )}
          </div>

          <div className="DetailsPage-buttons">
            <ButtonNew
              buttonClasses="tertiary-button back tour-back-to-trophic-levels-compare"
              label="Go back"
              // to={`${backUrl}`}
              onClick={goBack}
              iconLeft="backArrow"
            />
            {toggle === 'abundance' ? (
              <div className="DetailsPage-top-bar-left">
                <ButtonNew
                  buttonClasses="tertiary-button download"
                  label="Download data"
                  onClick={onDownload}
                  iconLeft={'exportIcon'}
                />
              </div>
            ) : null}
            <ButtonNew
              buttonClasses="secondary-button-small"
              label="Sensitivity Analysis"
              iconLeft="graphBar"
              to={
                isExperiment
                  ? `${urls.experiment(matrix)}/uncertainty`
                  : `${urls.matrix(matrix)}/uncertainty`
              }
            />
          </div>
        </div>

        <div className="DetailsPage-top-bar-right">
          <div className="DetailsPage-buttonset-container">
            <MenuTabs>
              {options.map((option, i) => (
                <Tab
                  key={i}
                  label={option.label}
                  onClick={() => setToggle(option.value)}
                  selected={option.value === toggle}
                  icon={option.icon}
                />
              ))}
            </MenuTabs>
          </div>
        </div>
      </div>
      {/* -------------------------------------------------------------------------------- */}

      <DetailsPageHeaderText tab={toggle} />
      {/* -------------------------------------------------------------------------------- */}
      <div className="DetailsPage-content" key={pointOfComparison}>
        {/* TODO come back to stats */}
        {toggle === 'stats' ? (
          <Stats
            base={base}
            matrix={matrix}
            isExperiment={isExperiment && pointOfComparison === 'experiment'}
          />
        ) : null}
        {toggle === 'abundance' ? (
          <AbundanceView
            base={base}
            matrix={matrix}
            pointOfComparison={pointOfComparison}
            ref={downloadRef}
          />
        ) : null}
      </div>
    </div>
  );
};

export default DetailsPage;
